<section class="admin-page" aria-label="Owner management">
  <header class="admin-header">
    <a class="back-link" routerLink="/home/settings">
      <mat-icon aria-hidden="true">arrow_back</mat-icon>
      Back to settings
    </a>
    <h1>Owner Management</h1>
    <p>Manage organization users, roles, and company-level details.</p>
  </header>

  <nav class="admin-tabs" aria-label="Management sections">
    <button type="button" mat-button color="primary" [class.active]="activeTab() === 'users'" (click)="setTab('users')">Manage Users</button>
    @if (canManageBugReports()) {
      <button type="button" mat-button color="primary" [class.active]="activeTab() === 'bugs'" (click)="setTab('bugs')">Reported Bugs</button>
    }
    @if (canManageCompany()) {
      <button type="button" mat-button color="primary" [class.active]="activeTab() === 'company'" (click)="setTab('company')">Manage Company</button>
    }
  </nav>

  @if (activeTab() === 'users') {
    <mat-card class="admin-card" aria-label="User management">
      <div class="admin-card__header">
        <h2>Users</h2>
        @if (canManageUsers()) {
          <button type="button" mat-flat-button color="primary" (click)="openAddUserDialog()"><mat-icon aria-hidden="true">person_add</mat-icon> Add user</button>
        }
      </div>

      <div class="admin-users-toolbar" aria-label="User filters and pagination controls">
        <mat-form-field appearance="outline" class="admin-users-search">
          <mat-label>Search users</mat-label>
          <input
            matInput
            type="text"
            placeholder="Name, email, or phone"
            [value]="searchTerm()"
            (input)="updateSearchTerm($any($event.target).value)"
          />
          <mat-icon matSuffix aria-hidden="true">search</mat-icon>
        </mat-form-field>

        <div class="admin-users-toolbar__meta">
          <label class="rows-per-page">
            <span>Rows per page</span>
            <select [value]="pageSize()" (change)="updatePageSize($any($event.target).value)">
              @for (size of pageSizeOptions; track size) {
                <option [value]="size">{{ size }}</option>
              }
            </select>
          </label>

          <p class="results-count">
            {{ visibleRangeStart() }}-{{ visibleRangeEnd() }} of {{ filteredUsersCount() }}
          </p>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (user of pagedUsers(); track user.email) {
              <tr>
                <td>{{ user.name }}</td>
                <td>{{ user.email }}</td>
                <td>{{ user.phone }}</td>
                <td>{{ user.role }}</td>
                <td>{{ user.status }}</td>
                <td>
                  <div class="table-actions">
                    <button type="button" class="table-action" mat-stroked-button color="primary" (click)="openEditUserDialog(user)">
                      Edit
                    </button>
                    @if (canToggleUserStatus(user)) {
                      <button type="button" class="table-action" mat-stroked-button color="primary" (click)="toggleUserStatus(user)">
                        {{ user.isActive ? 'Deactivate' : 'Activate' }}
                      </button>
                    }
                    @if (canDeleteUser(user)) {
                      <button type="button" class="table-action table-action--warn" mat-stroked-button (click)="deleteUser(user)">
                        Delete
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
            @if (pagedUsers().length === 0) {
              <tr>
                <td class="table-empty" colspan="6">No users found for "{{ searchTerm() }}".</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="admin-users-pagination" aria-label="Pagination">
        <button type="button" mat-stroked-button color="primary" (click)="previousPage()" [disabled]="isPreviousDisabled()">
          Previous
        </button>
        <p>Page {{ currentPage() }} of {{ totalPages() }}</p>
        <button type="button" mat-stroked-button color="primary" (click)="nextPage()" [disabled]="isNextDisabled()">
          Next
        </button>
      </div>
    </mat-card>
  } @else if (activeTab() === 'bugs') {
    <mat-card class="admin-card" aria-label="Reported bugs">
      <div class="admin-card__header">
        <div>
          <h2>Reported Bugs</h2>
          <p class="admin-card__subtitle">Review issues submitted by users in your organization.</p>
        </div>
      </div>

      <div class="admin-users-toolbar" aria-label="Bug report filters and pagination controls">
        <mat-form-field appearance="outline" class="admin-users-search">
          <mat-label>Search bug reports</mat-label>
          <input
            matInput
            type="text"
            placeholder="Title, description, reporter, page, or version"
            [value]="bugSearchTerm()"
            (input)="updateBugSearchTerm($any($event.target).value)"
          />
          <mat-icon matSuffix aria-hidden="true">search</mat-icon>
        </mat-form-field>

        <div class="admin-users-toolbar__meta">
          <label class="rows-per-page">
            <span>Rows per page</span>
            <select [value]="bugPageSize()" (change)="updateBugPageSize($any($event.target).value)">
              @for (size of pageSizeOptions; track size) {
                <option [value]="size">{{ size }}</option>
              }
            </select>
          </label>

          <p class="results-count">
            {{ visibleBugRangeStart() }}-{{ visibleBugRangeEnd() }} of {{ filteredBugReportsCount() }}
          </p>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Issue</th>
              <th>Reporter</th>
              <th>Page</th>
              <th>Version</th>
              <th>Created</th>
              <th>Screenshot</th>
            </tr>
          </thead>
          <tbody>
            @if (bugReportsLoading()) {
              <tr>
                <td class="table-empty" colspan="6">Loading bug reports…</td>
              </tr>
            } @else {
              @for (report of pagedBugReports(); track report.id) {
                <tr>
                  <td>
                    <div class="bug-report-cell">
                      <strong>{{ report.title }}</strong>
                      <p>{{ report.description }}</p>
                    </div>
                  </td>
                  <td>
                    <div class="bug-report-cell bug-report-cell--compact">
                      <strong>{{ report.reporterName }}</strong>
                      <p>{{ report.reporterEmail }}</p>
                    </div>
                  </td>
                  <td>
                    @if (report.pageUrl !== '-') {
                      <a class="table-link" [href]="report.pageUrl" target="_blank" rel="noreferrer">Open page</a>
                    } @else {
                      <span>-</span>
                    }
                  </td>
                  <td>{{ report.appVersion }}</td>
                  <td>{{ report.createdAt }}</td>
                  <td>
                    @if (report.screenshotUrl) {
                      <a class="table-link" [href]="report.screenshotUrl" target="_blank" rel="noreferrer">View screenshot</a>
                    } @else {
                      <span>-</span>
                    }
                  </td>
                </tr>
              }
              @if (pagedBugReports().length === 0) {
                <tr>
                  <td class="table-empty" colspan="6">No bug reports found for "{{ bugSearchTerm() }}".</td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <div class="admin-users-pagination" aria-label="Bug report pagination">
        <button type="button" mat-stroked-button color="primary" (click)="previousBugPage()" [disabled]="isPreviousBugDisabled()">
          Previous
        </button>
        <p>Page {{ bugCurrentPage() }} of {{ totalBugPages() }}</p>
        <button type="button" mat-stroked-button color="primary" (click)="nextBugPage()" [disabled]="isNextBugDisabled()">
          Next
        </button>
      </div>
    </mat-card>
  } @else {
    <mat-card class="admin-card" aria-label="Company management">
      <div class="admin-card__header">
        <h2>Company Details</h2>
      </div>

      <form class="company-form" [formGroup]="companyForm" (ngSubmit)="onSaveCompany()" novalidate>
        <mat-form-field appearance="outline">
          <mat-label>Company name</mat-label>
          <input matInput type="text" formControlName="name" />
          @if(companyForm.get('name')?.hasError('required')){
          <mat-error>Company name is required.</mat-error>
          }
          @if(companyForm.get('name')?.hasError('minlength')){
          <mat-error>Use at least 2 characters.</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Support email</mat-label>
          <input matInput type="email" formControlName="email" />
          @if(companyForm.get('email')?.hasError('required')){
          <mat-error>Email address is required.</mat-error>
          }
          @if(companyForm.get('email')?.hasError('email')){
          <mat-error>Enter a valid email address.</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Contact phone</mat-label>
          <input matInput type="tel" formControlName="phone" />
          @if(companyForm.get('phone')?.hasError('required')){
          <mat-error>Phone number is required.</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Address line 1</mat-label>
          <input matInput type="text" formControlName="address_line_1" />
          @if(companyForm.get('address_line_1')?.hasError('required')){
          <mat-error>Address line 1 is required.</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Address line 2</mat-label>
          <input matInput type="text" formControlName="address_line_2" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>City</mat-label>
          <input matInput type="text" formControlName="city" />
          @if(companyForm.get('city')?.hasError('required')){
          <mat-error>City is required.</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>State</mat-label>
          <input matInput type="text" formControlName="state" />
          @if(companyForm.get('state')?.hasError('required')){
          <mat-error>State is required.</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Country</mat-label>
          <input matInput type="text" formControlName="country" />
          @if(companyForm.get('country')?.hasError('required')){
          <mat-error>Country is required.</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Zip</mat-label>
          <input matInput type="text" formControlName="zip" />
          @if(companyForm.get('zip')?.hasError('required')){
          <mat-error>Zip is required.</mat-error>
          }
        </mat-form-field>

        <div class="company-form__section">Daily Meeting Provider</div>

        <mat-form-field appearance="outline">
          <mat-label>Daily API key</mat-label>
          <input matInput type="text" formControlName="daily_api_key" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Daily base URL</mat-label>
          <input matInput type="text" formControlName="daily_base_url" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Daily domain</mat-label>
          <input matInput type="text" formControlName="daily_domain" />
        </mat-form-field>

        <label class="company-form__checkbox">
          <mat-checkbox formControlName="daily_disabled">Disable Daily provider</mat-checkbox>
        </label>

        <div class="company-form__actions">
          <button type="button" mat-stroked-button color="primary" (click)="onCancelCompany()">Cancel</button>
          <button type="submit" mat-flat-button color="primary">Save changes</button>
        </div>

        @if(validationError().length){
        <app-validation-error [errors]="validationError()"></app-validation-error>
        }
      </form>
    </mat-card>
  }
</section>
